import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, MessageSquare, Mail, Tag, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { PHP_API } from '../config/api';

export default function AdminDashboard() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({
    total_products: 0,
    total_inquiries: 0,
    total_contacts: 0,
    pending_inquiries: 0,
    total_categories: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(PHP_API.dashboardStats, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Products', value: stats.total_products, icon: Package, color: 'bg-primary' },
    { label: 'Total Categories', value: stats.total_categories, icon: Tag, color: 'bg-secondary' },
    { label: 'Total Inquiries', value: stats.total_inquiries, icon: MessageSquare, color: 'bg-accent' },
    { label: 'Pending Inquiries', value: stats.pending_inquiries, icon: TrendingUp, color: 'bg-amber-500' },
    { label: 'Contact Messages', value: stats.total_contacts, icon: Mail, color: 'bg-blue-500' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl text-stone-900">Dashboard</h1>
        <p className="text-stone-600 mt-1">Welcome to the Dreamfield Admin Panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-soft"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-stone-500 text-sm">{stat.label}</p>
                  <p className="font-heading font-bold text-2xl text-stone-900">
                    {loading ? '...' : stat.value}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-soft">
        <h2 className="font-heading font-semibold text-lg text-stone-900 mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Add New Product', href: '/admin/products', icon: Package },
            { label: 'Manage Categories', href: '/admin/categories', icon: Tag },
            { label: 'View Inquiries', href: '/admin/inquiries', icon: MessageSquare },
            { label: 'View Contacts', href: '/admin/contacts', icon: Mail },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <a
                key={action.label}
                href={action.href}
                className="flex items-center gap-3 p-4 rounded-lg border border-stone-200 hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <Icon className="w-5 h-5 text-primary" />
                <span className="font-medium text-stone-700">{action.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}


